import { routesConfig } from '@src/core/routes';

/** Query param that records how the user entered the confirm-order step. */
export const CONFIRM_FLOW_ORIGIN_PARAM = 'from';

export const ConfirmFlowOrigin = {
  /** Step 3 of the create-mission wizard (route form → createMission). */
  WIZARD: 'wizard',
  /** Orders dashboard (e.g. READY_FOR_TAKE_OFF column). */
  ORDERS: 'orders',
} as const;

export type ConfirmFlowOrigin = typeof ConfirmFlowOrigin[keyof typeof ConfirmFlowOrigin];

export interface ConfirmOrderNavigationParams {
  origin: ConfirmFlowOrigin;
  droneSerial?: string | null;
  orderId?: string | number | null;
}

export function updateOrderPath(orderId: string | number): string {
  return `${routesConfig.orders}/update/${orderId}?step-by-step=true`;
}

export function updateMissionPath(
  missionId: string,
  options?: { stepByStep?: boolean; orderId?: string | number | null },
): string {
  const base = `${routesConfig.orders}/update-mission/${missionId}`;
  if (!options?.stepByStep && options?.orderId == null) {
    return base;
  }

  const search = new URLSearchParams();
  if (options.stepByStep) {
    search.set('step-by-step', 'true');
  }
  if (options.orderId != null) {
    search.set('orderId', String(options.orderId));
  }

  return `${base}?${search.toString()}`;
}

export function buildConfirmOrderPath(
  missionId: string,
  params: ConfirmOrderNavigationParams,
): string {
  const search = new URLSearchParams();
  search.set(CONFIRM_FLOW_ORIGIN_PARAM, params.origin);

  if (params.droneSerial) {
    search.set('droneSerial', params.droneSerial);
  }
  if (params.orderId != null) {
    search.set('orderId', String(params.orderId));
  }

  return `${routesConfig.confirmOrder}/${missionId}?${search.toString()}`;
}

export function parseConfirmFlowOrigin(
  searchParams: URLSearchParams,
): ConfirmFlowOrigin | null {
  const value = searchParams.get(CONFIRM_FLOW_ORIGIN_PARAM);

  if (value === ConfirmFlowOrigin.WIZARD || value === ConfirmFlowOrigin.ORDERS) {
    return value;
  }

  return null;
}

/**
 * Resolves where the confirm-order back action should navigate.
 * Wizard entry → edit existing mission route (replace); all other entries → orders dashboard.
 */
export interface ConfirmBackNavigation {
  path: string;
  replace: boolean;
}

export function resolveConfirmBackNavigation(
  origin: ConfirmFlowOrigin | null,
  missionId: string | undefined,
  orderId?: string | number | null,
): ConfirmBackNavigation {
  if (origin === ConfirmFlowOrigin.WIZARD && missionId) {
    return {
      path: updateMissionPath(missionId, { stepByStep: true, orderId }),
      replace: true,
    };
  }

  return { path: routesConfig.orders, replace: false };
}

/** Route step 3 before vs after mission exists on the API. */
export type MissionRouteBackContext = 'create-mission' | 'update-mission';

/**
 * Wizard back from step 3 (enter route):
 * - create-mission: mission not created yet → edit order (step 1)
 * - update-mission: mission already exists → orders dashboard
 */
export function resolveMissionRouteBackNavigation(
  context: MissionRouteBackContext,
  orderId: string | number | null | undefined,
  isStepByStep: boolean,
): ConfirmBackNavigation {
  if (!isStepByStep) {
    return { path: routesConfig.orders, replace: false };
  }

  if (context === 'create-mission' && orderId != null) {
    return { path: updateOrderPath(orderId), replace: true };
  }

  return { path: routesConfig.orders, replace: true };
}

/** After updating route in the wizard, return to confirm-order (step 4). */
export function resolveMissionRouteForwardNavigation(
  missionId: string,
  params: { orderId?: string | number | null; droneSerial?: string | null },
  isStepByStep: boolean,
): ConfirmBackNavigation {
  if (isStepByStep) {
    return {
      path: buildConfirmOrderPath(missionId, {
        origin: ConfirmFlowOrigin.WIZARD,
        orderId: params.orderId,
        droneSerial: params.droneSerial,
      }),
      replace: true,
    };
  }

  return { path: routesConfig.orders, replace: false };
}
